/**
 * Scope & tasks section inside the Rehab detail drawer.
 *
 * Mirrors the `.dr-task-list` block in rehab.html: one row per scope item
 * with a checkbox (done), label, budget (estimated), actual cost input, and
 * a delete affordance for user-added items. Standard line_items can't be
 * deleted — only their amounts (managed on the Find page).
 *
 * Source of truth is `property_setting.rehab` (line_items + custom_items +
 * progress). The drawer composes a flat `ScopeItem[]` from those parts and
 * passes mutation callbacks back here.
 */
import { useState } from "react";
import { ListChecks, Plus, X } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import {
  totalRecognizedSpend,
  type ScopeItem,
  visibleScopeItems,
} from "./rehab-scope";

function formatCurrency(v: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(v);
}

/**
 * `completed_at` is a full timestamp; the native date input wants a local
 * `YYYY-MM-DD`. Build the value from local date parts (not `.slice(0, 10)` on
 * the ISO string, which is UTC and can land on the wrong calendar day).
 */
function isoToDateValue(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Inverse of `isoToDateValue` — anchor the picked day at local noon so it
 * can't roll backward into the previous day when stored as a UTC timestamp. */
function dateValueToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(`${value}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

interface RehabScopeSectionProps {
  items: ScopeItem[];
  /** Toggle a task's done flag. */
  onToggleDone: (item: ScopeItem, done: boolean) => void;
  /**
   * Live edit of the actual cost — fires on every keystroke so the parent
   * can re-render the Budget vs Actual chart immediately. NOT persisted to
   * the network on each call.
   */
  onActualChange: (item: ScopeItem, value: number) => void;
  /**
   * Persist the actual cost — called when the field loses focus or the user
   * presses Enter. Fires unconditionally (the parent owns the changed /
   * unchanged decision, since `item.actual` here already carries the edit).
   */
  onActualCommit: (item: ScopeItem, value: number) => void;
  /**
   * Persist a completed task's completion date. `iso` is a full timestamp
   * (local noon of the picked day) or null when the field is cleared.
   */
  onCompletionDateChange: (item: ScopeItem, iso: string | null) => void;
  /** Append a new custom item with a name and estimated amount. */
  onAddCustom: (label: string, estimate: number) => void;
  /** Remove a custom item (and its progress entries). */
  onDeleteCustom: (item: ScopeItem) => void;
  /**
   * Whether task progress is editable (checkboxes + actual-cost inputs).
   * False for parked projects (in_rehab = false). Defaults to true.
   */
  canEdit?: boolean;
  /**
   * Whether the scope plan can be managed (add / delete custom tasks).
   * True when active, or when not-started (planning the scope before
   * starting). False for completed-and-moved-to-Properties (view only).
   * Defaults to true.
   */
  canManageCustom?: boolean;
}

export function RehabScopeSection({
  items,
  onToggleDone,
  onActualChange,
  onActualCommit,
  onCompletionDateChange,
  onAddCustom,
  onDeleteCustom,
  canEdit = true,
  canManageCustom = true,
}: RehabScopeSectionProps) {
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newEstimate, setNewEstimate] = useState("");

  // The custom task awaiting delete confirmation. Drives the warning modal —
  // null when no confirmation is pending.
  const [pendingDelete, setPendingDelete] = useState<ScopeItem | null>(null);

  // Local string drafts let the input accept partial values (e.g. "1." while
  // typing "1.5") without round-tripping through the parsed number prop. The
  // parsed number is forwarded to the parent on every keystroke via
  // `onActualChange` so the Budget vs Actual chart stays in sync.
  const [actualDrafts, setActualDrafts] = useState<Record<string, string>>({});

  // Hide line items with no estimated cost (budget 0) — empty scope entries
  // that would only clutter the task list. Totals still span all items.
  const visibleItems = visibleScopeItems(items);
  const totalBudget = items.reduce((s, i) => s + i.budget, 0);
  // Same recognition rule as the chart above — only completed tasks count
  // their actual toward this header, so the two summaries never disagree.
  // The per-row input below still shows each row's own recorded amount.
  const totalActual = totalRecognizedSpend(items);
  const doneCount = visibleItems.filter((i) => i.done).length;

  const closeAdd = () => {
    setAddOpen(false);
    setNewLabel("");
    setNewEstimate("");
  };

  const submitAdd = () => {
    const label = newLabel.trim();
    if (!label) return;
    const estimate = Math.max(0, Number(newEstimate) || 0);
    onAddCustom(label, estimate);
    closeAdd();
  };

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <ListChecks className="h-3.5 w-3.5 text-primary" aria-hidden />
          Scope &amp; tasks
        </span>
        <span className="text-xs font-semibold text-muted-foreground">
          {doneCount} / {visibleItems.length} · Budget {formatCurrency(totalBudget)} · Actual{" "}
          {formatCurrency(totalActual)}
        </span>
      </div>

      {visibleItems.length > 0 ? (
        <>
          <div className="mb-1 grid grid-cols-[18px_1fr_72px_92px_132px_22px] gap-2.5 px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <span />
            <span>Task</span>
            <span className="text-right">Budget</span>
            <span className="text-right">Actual</span>
            <span>Completion Date</span>
            <span />
          </div>
          <div className="flex flex-col gap-1.5">
            {visibleItems.map((item) => {
              const over = item.actual > item.budget && item.budget > 0;
              const draftValue =
                actualDrafts[item.key] !== undefined
                  ? actualDrafts[item.key]
                  : item.actual
                    ? String(item.actual)
                    : "";

              return (
                <div
                  key={item.key}
                  className={cn(
                    "grid grid-cols-[18px_1fr_72px_92px_132px_22px] items-center gap-2.5 rounded-md border border-border bg-card p-2 transition hover:border-border/80",
                    item.done && "text-muted-foreground",
                    item.isCustom && "border-dashed",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={item.done}
                    disabled={!canEdit}
                    onChange={(e) => onToggleDone(item, e.target.checked)}
                    className={cn(
                      "h-4 w-4 accent-primary",
                      canEdit ? "cursor-pointer" : "cursor-not-allowed",
                    )}
                    aria-label={`Mark "${item.label}" as ${
                      item.done ? "incomplete" : "done"
                    }`}
                  />
                  <span
                    className={cn(
                      "truncate text-sm",
                      item.done
                        ? "text-muted-foreground line-through"
                        : "text-foreground",
                    )}
                    title={item.label}
                  >
                    {item.label}
                    {item.isCustom && (
                      <span className="ml-2 inline-block rounded-full bg-primary/10 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-primary">
                        Custom
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      "text-right text-xs font-bold tabular-nums",
                      item.done
                        ? "text-muted-foreground/60"
                        : "text-muted-foreground",
                    )}
                  >
                    {formatCurrency(item.budget)}
                  </span>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md border border-border bg-muted/40 transition focus-within:border-primary",
                      over && "border-destructive bg-destructive/10",
                    )}
                    onClick={(e) => {
                      // Clicking the "$" / padding focuses the input for easier editing.
                      if (!canEdit) return;
                      const inp = e.currentTarget.querySelector("input");
                      if (inp && document.activeElement !== inp) inp.focus();
                    }}
                  >
                    <span className="pl-2 text-xs font-semibold text-muted-foreground">
                      $
                    </span>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      disabled={!canEdit}
                      value={draftValue}
                      placeholder="0"
                      aria-label={`Actual cost for "${item.label}"`}
                      onChange={(e) => {
                        const text = e.target.value;
                        setActualDrafts((prev) => ({ ...prev, [item.key]: text }));
                        // Mirror the parsed number up to the parent so the
                        // budget chart re-renders live on every keystroke.
                        onActualChange(item, Math.max(0, Number(text) || 0));
                      }}
                      onKeyDown={(e) => {
                        // Enter saves without making the user click away —
                        // dropping focus runs the same commit path as blur.
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.currentTarget.blur();
                        }
                      }}
                      onBlur={() => {
                        // Always hand the value up — `item.actual` already
                        // reflects this edit (the parent overlays the live
                        // draft), so diffing against it here would swallow
                        // every change. The parent dedupes against the last
                        // saved server value.
                        const next = Math.max(0, Number(draftValue) || 0);
                        onActualCommit(item, next);
                        setActualDrafts((prev) => {
                          const copy = { ...prev };
                          delete copy[item.key];
                          return copy;
                        });
                      }}
                      className="w-full min-w-0 bg-transparent px-2 py-1 text-right text-xs font-bold tabular-nums text-foreground outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </span>
                  <span className="min-w-0">
                    <DateInput
                      value={isoToDateValue(item.completedAt)}
                      disabled={!canEdit}
                      onChange={(e) =>
                        onCompletionDateChange(
                          item,
                          dateValueToIso(e.target.value),
                        )
                      }
                      className="h-8 px-2 text-xs"
                      aria-label={`Completion date for "${item.label}"`}
                    />
                  </span>
                  {item.isCustom && canManageCustom ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setPendingDelete(item);
                      }}
                      className="grid h-[22px] w-[22px] place-items-center rounded text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                      aria-label={`Remove ${item.label}`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="rounded-md border border-border bg-card p-3 text-center text-xs text-muted-foreground">
          No rehab line items with an estimated cost yet.
        </div>
      )}

      {/* Add-task affordance — solid primary so it reads as an action at rest,
          not only on hover. */}
      {canManageCustom && (
        <div className="mt-3">
          <Button
            type="button"
            size="sm"
            onClick={() => setAddOpen(true)}
            className="w-full font-bold shadow-sm"
          >
            <Plus aria-hidden /> Add Rehab Task
          </Button>
        </div>
      )}

      <Dialog
        open={addOpen}
        onOpenChange={(next) => (next ? setAddOpen(true) : closeAdd())}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Add rehab task</DialogTitle>
            <DialogDescription>
              Add a task to this rehab's scope with an estimated amount.
            </DialogDescription>
          </DialogHeader>

          <form
            id="add-custom-task"
            onSubmit={(e) => {
              e.preventDefault();
              submitAdd();
            }}
            className="flex flex-col gap-4 py-2"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="custom-task-name">Task name</Label>
              <Input
                id="custom-task-name"
                autoFocus
                value={newLabel}
                maxLength={60}
                placeholder="e.g. Window replacement"
                onChange={(e) => setNewLabel(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="custom-task-estimate">Estimate amount</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                  $
                </span>
                <Input
                  id="custom-task-estimate"
                  type="number"
                  min={0}
                  step="any"
                  value={newEstimate}
                  placeholder="0"
                  onChange={(e) => setNewEstimate(e.target.value)}
                  className="pl-6 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
              </div>
            </div>
          </form>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAdd}>
              Cancel
            </Button>
            <Button type="submit" form="add-custom-task" disabled={!newLabel.trim()}>
              Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove custom task confirmation */}
      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove custom task?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes{" "}
              <span className="font-semibold text-foreground">
                {pendingDelete?.label}
              </span>{" "}
              from the rehab scope, along with its budget and actual amounts.
              This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) onDeleteCustom(pendingDelete);
                setPendingDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
